import React, { Fragment, useContext, useEffect } from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Transition } from "@headlessui/react";
import {
  ArchiveBoxIcon,
  ArrowRightOnRectangleIcon,
  ChevronUpDownIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  CircleStackIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  FolderIcon,
  PrinterIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { UserContext } from "../../../utils/contexts/UserContext";
import TokenService from "../../../services/token.service";
import Brand from "../Brand/Brand";
import LanguageDropdown from "../../LanguageDropdown/LanguageDropdown";
import { canOpenPage } from "../../../utils/permissions";

// A link shows only to users whose permissions open its page (utils/permissions).
// `label` is a translation key, resolved with t() at render time.
// `alsoActiveOn` lists related pages that live outside the link's own path.
const links = [
  { to: "/my-questions", label: "nav.my_questions", Icon: PlusCircleIcon },
  { to: "/my-tasks", label: "nav.my_tasks", Icon: ListBulletIcon },
  { to: "/questions", label: "nav.question_bank", Icon: ArchiveBoxIcon },
  { to: "/folders", label: "nav.folders", Icon: FolderIcon },
  { to: "/my-evaluations", label: "nav.my_evaluations", Icon: ClipboardDocumentCheckIcon },
  { to: "/evaluators", label: "nav.evaluators", Icon: UsersIcon },
  { to: "/available-exams", label: "nav.available_exams", Icon: AcademicCapIcon },
  { to: "/my-attempts", label: "nav.my_attempts", Icon: DocumentTextIcon },
  { to: "/test_list", label: "nav.test_bank", Icon: CircleStackIcon, alsoActiveOn: ["/test_create", "/test/"] },
  { to: "/exams_list", label: "nav.exam_bank", Icon: ClipboardDocumentListIcon, alsoActiveOn: ["/exams_create"] },
  { to: "/bts", label: "nav.bts", Icon: PrinterIcon },
];

// On md+ the sidebar is a full-height column; below md it is an off-canvas drawer controlled by `open`.
export default function Sidebar({ open = false, onClose = () => {} }) {
  const [t] = useTranslation();
  const { user, setUser } = useContext(UserContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const name = user?.username || t("nav.user");
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  const logout = () => {
    TokenService.removeTokens();
    setUser(null);
    navigate("/login");
  };

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-30 bg-slate-900/40 transition-opacity duration-200 md:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white shadow-xl transition-[transform,visibility] duration-200 ease-out md:static md:z-auto md:translate-x-0 md:shadow-none ${
          open ? "translate-x-0" : "-translate-x-full max-md:invisible"
        }`}
      >
        <Brand className="h-16 shrink-0 px-5" />

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-3">
          {links
            .filter(({ to }) => canOpenPage(user, to))
            .map(({ to, label, Icon, alsoActiveOn }) => {
              const active =
                !!matchPath({ path: to, end: false }, pathname) ||
                !!alsoActiveOn?.some((path) => pathname.startsWith(path));

              return (
                <Link
                  key={to}
                  to={to}
                  onClick={onClose}
                  aria-current={active ? "page" : undefined}
                  className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                    active ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`absolute inset-y-1.5 -left-3 w-1 rounded-r-full bg-blue-600 transition-opacity duration-200 ${
                      active ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <Icon
                    className={`h-5 w-5 shrink-0 transition-colors duration-200 ${
                      active ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"
                    }`}
                    aria-hidden="true"
                  />
                  {t(label)}
                </Link>
              );
            })}
        </nav>

        <LanguageDropdown variant="nav" className="mx-3 mb-2 shrink-0" />

        {!!user && (
          <Menu as="div" className="relative shrink-0 border-t border-slate-200 p-3">
            <Menu.Button className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition duration-200 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ui-open:bg-slate-100">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 text-sm font-semibold uppercase text-blue-700">
                {name.charAt(0)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-900">
                  {name}
                </span>
                {fullName && (
                  <span className="block truncate text-xs text-slate-500">
                    {fullName}
                  </span>
                )}
              </span>
              <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition duration-150 ease-out"
              enterFrom="translate-y-1 scale-95 opacity-0"
              enterTo="translate-y-0 scale-100 opacity-100"
              leave="transition duration-100 ease-in"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <Menu.Items className="absolute inset-x-3 bottom-full mb-1 origin-bottom rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-900/5 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ${
                        active ? "bg-red-50 text-red-600" : "text-slate-700"
                      }`}
                      onClick={logout}
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                      {t("buttons.logout")}
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </aside>
    </>
  );
}
